/** @jsx jsx */
import CircularProgress from '@material-ui/core/CircularProgress'
import * as Sentry from '@sentry/browser'
import env from 'lib/env'
import {Fragment, useState} from 'react'
import {toast} from 'react-toastify'
import {jsx} from 'theme-ui'
import Button from './Button'
import CancelSubscriptionConfirmation from './CancelSubscriptionConfirmation'
import {useTranslation} from 'react-i18next'
import ky from 'ky'

type Props = {
  callback: () => Promise<boolean>
  subscriptionId: string
  token: string | null
  paymentGateway: 'STRIPE' | 'PAYPAL'
}

const CancelSubscriptionButton: React.FC<Props> = ({
  callback,
  subscriptionId,
  token,
  paymentGateway,
}) => {
  const {t} = useTranslation()
  const [loading, setLoading] = useState(false)
  const [isConfirmationOpen, setConfirmationOpen] = useState(false)
  const closeConfirmation = () => {
    setConfirmationOpen(false)
  }
  const openConfirmation = () => {
    setConfirmationOpen(true)
  }
  const handleConfirm = async () => {
    closeConfirmation()
    setLoading(true)
    if (!token) {
      throw new Error("We don't have any token")
    }
    const res = await ky(
      `${
        env.GATSBY_DONATION_API
      }auth/${paymentGateway.toLowerCase()}/subscriptions/${subscriptionId}/cancel/`,
      {
        method: 'POST',
        headers: {
          Authorization: `Token ${token}`,
        },
      },
    )
    const data: {success: boolean} = await res.json()
    if (data && data.success) {
      await callback()
      toast(t('subscriptionCancellationSuccess'))
    } else {
      Sentry.captureMessage(`Could not cancel a subscription ${subscriptionId}`)
      toast(t('subscriptionCancellationFailure'))
    }
    setLoading(false)
  }
  return (
    <Fragment>
      <CancelSubscriptionConfirmation
        open={isConfirmationOpen}
        handleYes={handleConfirm}
        handleNo={closeConfirmation}
      />
      {loading ? (
        <div
          sx={{
            textAlign: 'center',
          }}
        >
          <CircularProgress size={20} />
        </div>
      ) : (
        <Button
          disabled={loading}
          onClick={openConfirmation}
          inverse
          sx={{
            px: 10,
            py: 5,
            borderColor: 'primary',
            borderStyle: 'solid',
            borderWidth: 1,
            fontSize: 12,
          }}
        >
          Cancel
        </Button>
      )}
    </Fragment>
  )
}

export default CancelSubscriptionButton
